import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { ArrowLeft, ArrowRight, Home, Check } from 'lucide-react';
import { createOwnerComplete } from '../../../lib/ownerCreation';
import { supabase } from '../../../lib/supabase';
import { useAppStore } from '../../../lib/store';
import { isRTL } from '../../../lib/i18n';
import { propertiesApi } from '../../../lib/api';
import { Tables } from '../../../lib/database.types';

export default function AddOwner() {
  const { t } = useTranslation(['owners', 'common']);
  const navigate = useNavigate();
  const language = useAppStore((state) => state.settings.language);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    password: '',
    confirmPassword: '',
    address: '',
    city: '',
    country: '',
    nationality: '',
    id_number: '',
    is_foreign: false,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Property assignment state
  const [availableProperties, setAvailableProperties] = useState<Tables<'properties'>[]>([]);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(true);

  // Fetch available properties on mount
  useEffect(() => {
    const fetchProperties = async () => {
      setLoadingProperties(true);
      try {
        const response = await propertiesApi.getAvailableForAssignment();
        if (response.data) {
          setAvailableProperties(response.data);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoadingProperties(false);
      }
    };
    fetchProperties();
  }, []);

  // Toggle property selection
  const togglePropertySelection = (propertyId: string) => {
    setSelectedPropertyIds(prev => {
      if (prev.includes(propertyId)) {
        return prev.filter(id => id !== propertyId);
      }
      return [...prev, propertyId];
    });
  };

  const handleChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
    // Clear error when user types
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleCheckboxChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.checked });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields validation
    if (!formData.first_name.trim()) {
      newErrors.first_name = t('common:required');
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = t('common:required');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('common:required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('common:invalidEmail');
    }

    if (!formData.phone_number.trim()) {
      newErrors.phone_number = t('common:required');
    }

    if (!formData.password) {
      newErrors.password = t('common:required');
    } else if (formData.password.length < 6) {
      newErrors.password = t('common:passwordTooShort');
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('common:required');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('common:passwordsDoNotMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Check for duplicate email
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('email')
        .eq('email', formData.email.trim().toLowerCase())
        .maybeSingle();

      if (existingUser) {
        setErrors({ email: t('common:emailAlreadyExists') });
        setSubmitError(t('common:emailAlreadyExists'));
        setLoading(false);
        return;
      }

      // Create owner account
      const result = await createOwnerComplete({
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        phone: formData.phone_number.trim() || undefined,
        address: formData.address.trim() || undefined,
        city: formData.city.trim() || undefined,
        country: formData.country.trim() || undefined,
        nationality: formData.nationality.trim() || undefined,
        id_number: formData.id_number.trim() || undefined,
        is_foreign: formData.is_foreign,
      });

      if (!result.success) {
        setSubmitError(result.error || t('common:error'));
        setLoading(false);
        return;
      }

      // Assign selected properties to the new owner (if any selected)
      let propertyMessage = '';
      if (selectedPropertyIds.length > 0 && result.userId) {
        const assignResult = await propertiesApi.assignToOwner(selectedPropertyIds, result.userId);
        if (assignResult.data) {
          const { success, failed } = assignResult.data;
          if (success > 0) {
            propertyMessage = `\n${t('propertiesAssigned', { count: success })}`;
          }
          if (failed > 0) {
            propertyMessage += `\n${t('propertyAssignmentFailed', { count: failed })}`;
          }
        } else if (assignResult.error) {
          propertyMessage = `\n${t('propertyAssignmentError', { message: assignResult.error.message })}`;
        }
      }

      // Success!
      alert(t('ownerAdded') + propertyMessage);
      navigate('/dashboard/owners');
    } catch (error: any) {
      console.error('Error creating owner:', error);
      setSubmitError(error.message || t('common:error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box dir={language === 'ar' ? 'rtl' : 'ltr'} sx={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          {...(isRTL()
            ? { endIcon: <ArrowRight size={20} /> }
            : { startIcon: <ArrowLeft size={20} /> }
          )}
          onClick={() => navigate('/dashboard/owners')}
        >
          {t('common:back')}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {t('addOwner')}
        </Typography>
      </Box>

      {/* Form */}
      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Personal Information Section */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  {t('personalInfo')}
                </Typography>
              </Grid>

              {/* First Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={t('firstName')}
                  value={formData.first_name}
                  onChange={handleChange('first_name')}
                  error={!!errors.first_name}
                  helperText={errors.first_name}
                  disabled={loading}
                  inputProps={{
                    dir: language === 'ar' ? 'rtl' : 'ltr',
                    style: { textAlign: language === 'ar' ? 'right' : 'left' }
                  }}
                />
              </Grid>

              {/* Last Name */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={t('lastName')}
                  value={formData.last_name}
                  onChange={handleChange('last_name')}
                  error={!!errors.last_name}
                  helperText={errors.last_name}
                  disabled={loading}
                  inputProps={{
                    dir: language === 'ar' ? 'rtl' : 'ltr',
                    style: { textAlign: language === 'ar' ? 'right' : 'left' }
                  }}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label={t('email')}
                  value={formData.email}
                  onChange={handleChange('email')}
                  error={!!errors.email}
                  helperText={errors.email}
                  disabled={loading}
                  inputProps={{
                    dir: language === 'ar' ? 'rtl' : 'ltr',
                    style: { textAlign: language === 'ar' ? 'right' : 'left' }
                  }}
                />
              </Grid>

              {/* Phone Number */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label={t('phoneNumber')}
                  value={formData.phone_number}
                  onChange={handleChange('phone_number')}
                  placeholder="+966501234567"
                  error={!!errors.phone_number}
                  helperText={errors.phone_number}
                  disabled={loading}
                  inputProps={{
                    dir: language === 'ar' ? 'rtl' : 'ltr',
                    style: { textAlign: language === 'ar' ? 'right' : 'left' }
                  }}
                />
              </Grid>

              {/* Password */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="password"
                  label={t('password')}
                  value={formData.password}
                  onChange={handleChange('password')}
                  error={!!errors.password}
                  helperText={errors.password}
                  disabled={loading}
                  inputProps={{
                    dir: language === 'ar' ? 'rtl' : 'ltr',
                    style: { textAlign: language === 'ar' ? 'right' : 'left' }
                  }}
                />
              </Grid>

              {/* Confirm Password */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="password"
                  label={t('confirmPassword')}
                  value={formData.confirmPassword}
                  onChange={handleChange('confirmPassword')}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  disabled={loading}
                  inputProps={{
                    dir: language === 'ar' ? 'rtl' : 'ltr',
                    style: { textAlign: language === 'ar' ? 'right' : 'left' }
                  }}
                />
              </Grid>

              {/* ID Number */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('idNumber')}
                  value={formData.id_number}
                  onChange={handleChange('id_number')}
                  disabled={loading}
                  inputProps={{
                    dir: language === 'ar' ? 'rtl' : 'ltr',
                    style: { textAlign: language === 'ar' ? 'right' : 'left' }
                  }}
                />
              </Grid>

              {/* Nationality */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('nationality')}
                  value={formData.nationality}
                  onChange={handleChange('nationality')}
                  disabled={loading}
                  inputProps={{
                    dir: language === 'ar' ? 'rtl' : 'ltr',
                    style: { textAlign: language === 'ar' ? 'right' : 'left' }
                  }}
                />
              </Grid>

              {/* Address Information Section */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, color: 'primary.main' }}>
                  {t('addressInfo')}
                </Typography>
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('address')}
                  value={formData.address}
                  onChange={handleChange('address')}
                  disabled={loading}
                  inputProps={{
                    dir: language === 'ar' ? 'rtl' : 'ltr',
                    style: { textAlign: language === 'ar' ? 'right' : 'left' }
                  }}
                />
              </Grid>

              {/* City */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('city')}
                  value={formData.city}
                  onChange={handleChange('city')}
                  disabled={loading}
                  inputProps={{
                    dir: language === 'ar' ? 'rtl' : 'ltr',
                    style: { textAlign: language === 'ar' ? 'right' : 'left' }
                  }}
                />
              </Grid>

              {/* Country */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('country')}
                  value={formData.country}
                  onChange={handleChange('country')}
                  disabled={loading}
                  inputProps={{
                    dir: language === 'ar' ? 'rtl' : 'ltr',
                    style: { textAlign: language === 'ar' ? 'right' : 'left' }
                  }}
                />
              </Grid>

              {/* Additional Info Section */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, color: 'primary.main' }}>
                  {t('additionalInfo')}
                </Typography>
              </Grid>

              {/* Foreign Owner Checkbox */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_foreign}
                      onChange={handleCheckboxChange('is_foreign')}
                      disabled={loading}
                    />
                  }
                  label={t('isForeign')}
                />
              </Grid>

              {/* Property Assignment Section (Optional) */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'secondary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Home size={20} />
                  {t('assignProperties')} ({t('common:optional')})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('assignPropertiesDescription')}
                </Typography>

                {loadingProperties ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : availableProperties.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    {t('noPropertiesAvailable')}
                  </Typography>
                ) : (
                  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                    <List dense>
                      {availableProperties.map((property) => {
                        const isSelected = selectedPropertyIds.includes(property.id);
                        return (
                          <ListItem key={property.id} disablePadding>
                            <ListItemButton
                              onClick={() => togglePropertySelection(property.id)}
                              selected={isSelected}
                              disabled={loading}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox
                                  edge="start"
                                  checked={isSelected}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={property.title}
                                secondary={`${property.address}${property.city ? `, ${property.city}` : ''}`}
                              />
                              <Chip
                                label={t(`properties:status.${property.status}`, property.status)}
                                size="small"
                                color={property.status === 'available' ? 'success' : 'default'}
                                sx={{ ml: 1 }}
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                )}

                {selectedPropertyIds.length > 0 && (
                  <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 500 }}>
                    {t('selectedProperties', { count: selectedPropertyIds.length })}
                  </Typography>
                )}
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/dashboard/owners')}
                    disabled={loading}
                  >
                    {t('common:cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {loading ? t('common:creating') : t('addOwner')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
